import React from "react";
import circleLogo from "../assets/circleLogo.png";
import { ChatMessage } from "../types/chat";
import {
  makeResolveVirtualRequest,
  getMediaFromChatMessage,
  getAudioFromChatMessage,
} from "../services/ChatService";
import MessageControls from "./MessageControls";
import { processCustomTags, formatMessageContent } from "../utils/textUtils";
import { MediaDisplay } from "./MediaDisplay";
import { useAuth } from "../providers/AuthContext";
import { useAudio } from "../providers/AudioProvider";
import { useMessageActions } from "../hooks/useMessageActions";
import { CHAT_CONSTANTS } from "../utils/constants";
import OptionButtons from "./OptionButtons";
import SuggestedOptions from "./SuggestedNextTopics";

interface MessageItemProps extends ChatMessage {
  // All props from ChatMessage (including id and messageId)
  loadMedia?: boolean;
  isReadOnly?: boolean;
  hideLogo?: boolean;
  disabledOptions?: boolean[]; // Array indicating which options should be disabled
  isLastMessage?: boolean; // Indicates if this is the last message in the conversation
}

const MessageItem: React.FC<MessageItemProps> = ({
  id,
  messageId,
  text,
  dateTime,
  origin,
  messageType,
  nodeUuid,
  options,
  audio,
  media,
  preFetchedMediaBlob,
  preFetchedAudioBlob,
  preFetchedMediaUrl,
  isFlagged,
  loadMedia = true,
  isReadOnly = false,
  hideLogo = false,
  isLastMessage = false,
}) => {
  const { getUserEmail } = useAuth();
  const { playResponseOutloud } = useAudio();
  const { onSpanClick } = useMessageActions();

  // Use pre-fetched media and audio if available, otherwise fall back to fetching
  const [mediaBlob, setMediaBlob] = React.useState<
    | Blob
    | {
        url: string;
        media_type: string;
        content_type: string;
        elapsed_time: number;
      }
    | null
  >(preFetchedMediaBlob || null);
  const [audioBlob, setAudioBlob] = React.useState<Blob | null>(
    preFetchedAudioBlob || null
  );
  const [mediaUrl, setMediaUrl] = React.useState<string | null>(
    preFetchedMediaUrl || null
  );
  const [isLoadingMedia, setIsLoadingMedia] = React.useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = React.useState(false);

  // Keep a ref to prevent garbage collection of blobs in Chrome
  const audioBlobRef = React.useRef<Blob | null>(audioBlob);
  const mediaBlobRef = React.useRef<Blob | null>(null);

  // Update blob refs when blobs change
  React.useEffect(() => {
    audioBlobRef.current = audioBlob;
    if (mediaBlob instanceof Blob) {
      mediaBlobRef.current = mediaBlob;
    }
  }, [audioBlob, mediaBlob]);

  // Only fetch media and audio if not pre-fetched and loadMedia is true
  React.useEffect(() => {
    const fetchMediaAndAudio = async () => {
      // Only fetch if not pre-fetched and loadMedia is true
      if (media && messageType === "bot" && !preFetchedMediaBlob && loadMedia) {
        setIsLoadingMedia(true);
        try {
          const result = await getMediaFromChatMessage({
            id: id,
            messageId: messageId || id,
            text,
            dateTime,
            messageType,
            media,
            audio: Array.isArray(audio) ? audio : [audio || ""],
          });
          setMediaBlob(result);

          // Handle different response types
          if (result instanceof Blob) {
            setMediaUrl(URL.createObjectURL(result));
          } else if (result && typeof result === "object" && "url" in result) {
            setMediaUrl(result.url);
          }
        } catch (error) {
          console.error("Error fetching media:", error);
        } finally {
          setIsLoadingMedia(false);
        }
      }

      if (
        audio &&
        audio.length > 0 &&
        messageType === "bot" &&
        !preFetchedAudioBlob &&
        loadMedia
      ) {
        setIsLoadingAudio(true);
        try {
          const result = await getAudioFromChatMessage({
            id: id,
            messageId: messageId || id,
            text,
            dateTime,
            messageType,
            media: media || "",
            audio,
          });
          setAudioBlob(result);
        } catch (error) {
          console.error("Error fetching audio:", error);
        } finally {
          setIsLoadingAudio(false);
        }
      }
    };

    fetchMediaAndAudio();
  }, [
    media,
    audio,
    messageType,
    nodeUuid,
    text,
    dateTime,
    id,
    messageId,
    preFetchedMediaBlob,
    preFetchedAudioBlob,
    loadMedia,
  ]);

  // Cleanup function to revoke object URLs
  React.useEffect(() => {
    return () => {
      if (mediaUrl && mediaBlob instanceof Blob && !preFetchedMediaUrl) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [mediaUrl, mediaBlob, preFetchedMediaUrl]);

  const handleSpanClick = async (
    dataId: string,
    spanText: string,
    elementId: string,
    dataInfo: string
  ) => {
    if (onSpanClick) {
      await onSpanClick(dataId, spanText, elementId, dataInfo);
    } else {
      makeResolveVirtualRequest({
        nodeUUID: dataId,
        textClicked: spanText,
        currentNodeId: nodeUuid,
        userEmail: getUserEmail(),
        logRequest: true,
        prefix: "benchmark",
        endpointResult:
          dataInfo == CHAT_CONSTANTS.DEFINITION_ROOT && dataId != dataInfo
            ? "definition"
            : "",
      });
    }
  };

  // Remove STT prefix from display for user messages while preserving it in the original text
  const displayText =
    messageType === "user" && text.startsWith("STT: ")
      ? text.substring(5) // Remove "STT: " prefix
      : text;

  const formattedContent = formatMessageContent(displayText, isReadOnly);
  const showMediaPlaceholder = media && !loadMedia;
  let isVideo = false;
  if (showMediaPlaceholder) {
    const mediaPath = typeof media === "string" ? media.toLowerCase() : "";
    isVideo = mediaPath.endsWith(".mp4") || mediaPath.includes(".mp4");
  }

  return (
    <div
      className={`flex flex-col ${messageType === "bot" ? "justify-start" : "justify-end"} mb-2 sm:mb-3 lg:mb-4`}
    >
      <div
        className={`flex items-end gap-1 sm:gap-2 lg:gap-3 ${messageType === "bot" ? "flex-row" : "flex-row-reverse"}`}
      >
        {/* Circle Logo - only for bot messages, positioned outside gray box */}
        {!hideLogo && messageType === "bot" && (
          <img
            src={circleLogo}
            alt="Bot"
            className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full flex-shrink-0 hidden sm:block"
          />
        )}

        {/* Main Message Content Box */}
        <div
          className={`rounded-2xl sm:rounded-3xl p-1 max-w-[85vw] sm:max-w-[70vw] lg:max-w-[60vw] ${
            messageType === "bot"
              ? "bg-gray-100 text-gray-800"
              : "bg-[#46ACC6] text-white"
          }`}
          style={{
            borderBottomRightRadius:
              messageType === "bot" ? "1.5rem" : "0.2rem",
            borderTopRightRadius: "1.5rem",
            borderTopLeftRadius: "1.5rem",
            borderBottomLeftRadius: messageType === "bot" ? "0.2rem" : "1.5rem",
            background: messageType === "bot" ? "#f3f4f6" : "#46ACC6",
          }}
        >
          <div className="rounded-2xl sm:rounded-3xl flex-1 min-w-0 p-2">
            {/* Message Content */}
            <div
              className="text-xs sm:text-sm lg:text-base leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: formattedContent }}
              data-trust-trigger="message-content"
              onClick={
                isReadOnly
                  ? () => {
                      console.log("read only");
                    }
                  : async (e) => {
                      // Handle clicks on custom-link spans
                      const target = e.target as HTMLElement;
                      if (target.classList.contains("custom-link")) {
                        const dataId = target.getAttribute("data-id");
                        const dataInfo = target.getAttribute("data-info");
                        const spanText = target.textContent;
                        if (dataId && spanText && dataInfo) {
                          e.preventDefault();
                          await handleSpanClick(
                            dataId,
                            spanText,
                            id || "",
                            dataInfo
                          );
                        }
                      }
                    }
              }
            />

            {/* Media Placeholder (if media exists but loadMedia is false) */}
            {showMediaPlaceholder && (
              <div className="my-2 flex items-center justify-center">
                <div className="flex flex-col items-center p-3 sm:p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 w-full max-w-[250px] sm:max-w-[300px]">
                  {isVideo ? (
                    <svg
                      className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="2"
                        strokeWidth={2}
                        stroke="currentColor"
                        fill="none"
                      />
                      <circle
                        cx="8.5"
                        cy="10.5"
                        r="2"
                        strokeWidth={2}
                        stroke="currentColor"
                        fill="none"
                      />
                      <path
                        d="M21 19l-5.5-7-4.5 6-3-4-4 5"
                        strokeWidth={2}
                        stroke="currentColor"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  <span className="text-gray-500 text-xs sm:text-sm">
                    {isVideo ? "Video" : "Image"} available
                  </span>
                </div>
              </div>
            )}

            {/* Media Display */}
            {media && (mediaUrl || isLoadingMedia) && loadMedia && (
              <MediaDisplay
                mediaUrl={mediaUrl}
                onVideoEnd={() => {}}
                mediaId={messageId}
                originalMediaPath={media}
                onMediaUrlUpdate={(newUrl) => {
                  // Update the mediaUrl state when MediaDisplay refreshes the URL
                  setMediaUrl(newUrl);

                  // Also update the mediaBlob state to reflect the new URL response
                  setMediaBlob({
                    url: newUrl,
                    media_type: "video",
                    content_type: "video/mp4",
                    elapsed_time: 0,
                  });
                }}
              />
            )}

            {/* Option Buttons */}
            {messageType === "bot" && options && options.length > 0 && (
              <OptionButtons
                options={options}
                parentNodeUuid={nodeUuid || ""}
                isReadOnly={isReadOnly}
                elementId={id}
                isDefinitionNode={false} // isDefinitionNode is false unless rendered in definition modal
              />
            )}

            {/* DateTime and Origin */}
            <div className="mt-2 text-xs text-gray-500">
              <div>{dateTime.toLocaleTimeString()}</div>
              {origin && <div>Origin: {processCustomTags(origin)}</div>}
            </div>
          </div>
        </div>

        {/* Message Controls - only for bot messages, positioned outside gray box */}
        {messageType === "bot" && (
          <MessageControls
            audioBlob={audioBlob}
            isLoadingAudio={isLoadingAudio}
            queryId={messageId || id}
            autoPlay={playResponseOutloud}
            isFlagged={isFlagged}
          />
        )}
      </div>
      {(isReadOnly || isLastMessage) && (
        <div className="pl-2 lg:pl-9">
          {messageType === "bot" && options && options.length > 0 && (
            <SuggestedOptions
              options={options}
              parentNodeUuid={nodeUuid || ""}
              isReadOnly={isReadOnly}
              elementId={id}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(MessageItem);
